/**
 * Extração centralizada da mensagem de erro vinda da API.
 *
 * Muitos `catch` espalhados pela app descartavam o erro real e mostravam
 * strings genéricas ("Ocorreu um erro"), o que quebra a UX porque o
 * utilizador não sabe o que falhou. Este helper recupera a mensagem que o
 * backend enviou (`response.data.message`) e só cai no fallback quando
 * realmente não há nada de útil.
 */
import { AxiosError } from "axios";
import { t } from "@/shared/i18n";

/**
 * Erro por campo emitido pelo `GlobalExceptionHandler` do backend
 * (`ApiError.FieldError`): validação @Valid, campos pendentes, query param
 * em falta ou com tipo incompatível.
 */
interface ApiFieldError {
    field: string;
    message: string;
}

/** Formatos comuns de corpo de erro retornados pelo backend. */
interface ApiErrorBody {
    message?: string | string[];
    error?: string;
    /** Formato canónico deste backend (`ApiError.fields`). */
    fields?: ApiFieldError[];
    /** Formato legado/externo (NestJS, class-validator). */
    errors?: Record<string, string[] | string> | string[];
    detail?: string;
    /** Chave estável do erro: o gateway e o chat mandam sempre; o gestaodeobras só em alguns. */
    code?: string;
}

/** Chave i18n + texto por omissão, para não depender de recursos carregados. */
function tr(key: string, fallback: string): string {
    return t(`apiErrors.${key}`, { defaultValue: fallback });
}

const DEFAULT_FALLBACK_KEY = "unexpected";
const DEFAULT_FALLBACK_TEXT = "Ocorreu um erro inesperado. Tente novamente.";

/**
 * Retorna a mensagem de erro mais específica disponível.
 *
 * @param error            O erro capturado (axios, Error, string, etc.).
 * @param fallbackOverride Contexto da operação a usar quando o corpo da
 *                         resposta não traz nada de útil (ex.: "Erro ao
 *                         guardar projeto"). Omitido, usa o genérico i18n.
 */
export function getApiErrorMessage(error: unknown, fallbackOverride?: string): string {
    // Resolvido a cada chamada (e não uma vez no módulo) para acompanhar
    // mudanças de locale em runtime.
    const fallback = fallbackOverride ?? tr(DEFAULT_FALLBACK_KEY, DEFAULT_FALLBACK_TEXT);

    // Erro de rede / timeout do axios (sem resposta do servidor)
    const axiosErr = error as AxiosError<ApiErrorBody> | undefined;

    if (axiosErr?.isAxiosError) {
        if (axiosErr.response) {
            const data = axiosErr.response.data;
            const fromBody = pickMessage(data);
            if (fromBody) return fromBody;
            // Sem mensagem útil no corpo: combinar o contexto da operação
            // (fallback do call-site, ex. "Erro ao salvar projeto") com o
            // motivo traduzido do código de status HTTP. Assim o utilizador
            // vê O QUÊ falhou e PORQUÊ, em vez de "status code 403".
            const reason = messageForStatus(axiosErr.response.status);
            if (reason) return combineContextAndReason(fallback, reason);
        } else if (axiosErr.code === "ECONNABORTED") {
            return tr("timeout", "O servidor demorou a responder. Verifique a sua ligação e tente novamente.");
        } else if (axiosErr.request) {
            return tr("offline", "Sem ligação ao servidor. Verifique a sua internet e tente novamente.");
        }
    }

    // Evitar expor a mensagem crua do axios ("Request failed with status code 403").
    if (error instanceof Error && error.message && !/status code \d+/i.test(error.message)) {
        return error.message;
    }

    if (typeof error === "string" && error.trim()) {
        return error;
    }

    return fallback;
}

/**
 * Junta o contexto da operação (o que o utilizador estava a fazer) com o
 * motivo do erro. Ex.: ("Erro ao salvar projeto.", "Não tem permissão...")
 * -> "Erro ao salvar projeto: não tem permissão...".
 * Se o fallback for o genérico, devolve só o motivo (mais limpo).
 */
function combineContextAndReason(fallback: string, reason: string): string {
    const context = fallback.trim();
    const genericFallback = tr(DEFAULT_FALLBACK_KEY, DEFAULT_FALLBACK_TEXT);
    if (!context || context === genericFallback || context === DEFAULT_FALLBACK_TEXT) return reason;

    // Remover pontuação final do contexto e ligar com ": " minúsculo.
    const cleanContext = context.replace(/[.:!?\s]+$/, "");
    const reasonLower = reason.charAt(0).toLowerCase() + reason.slice(1);
    return `${cleanContext}: ${reasonLower}`;
}

/**
 * Mensagens amigáveis por código de status HTTP, para quando o backend não
 * envia nada de útil no corpo. O utilizador não percebe "status code 403".
 */
function messageForStatus(status: number): string | null {
    switch (status) {
        case 400:
            return tr("http.400", "Pedido inválido. Verifique os dados e tente novamente.");
        case 401:
            return tr("http.401", "Sessão expirada ou credenciais inválidas. Inicie sessão novamente.");
        case 403:
            return tr("http.403", "Não tem permissão para realizar esta ação.");
        case 404:
            return tr("http.404", "Não foi possível encontrar o que procurava.");
        case 405:
            return tr("http.405", "Operação não permitida.");
        case 408:
            return tr("http.408", "O pedido demorou demasiado tempo. Tente novamente.");
        case 409:
            return tr("http.409", "Já existe um registo com estes dados.");
        case 413:
            return tr("http.413", "O ficheiro ou pedido é demasiado grande.");
        case 422:
            return tr("http.422", "Alguns dados são inválidos. Reveja o formulário e tente novamente.");
        case 429:
            return tr("http.429", "Demasiados pedidos. Aguarde um momento e tente novamente.");
        case 500:
            return tr("http.500", "Ocorreu um erro no servidor. Tente novamente mais tarde.");
        case 502:
        case 503:
        case 504:
            return tr("http.503", "O serviço está temporariamente indisponível. Tente novamente mais tarde.");
        default:
            if (status >= 500) return tr("http.500", "Ocorreu um erro no servidor. Tente novamente mais tarde.");
            if (status >= 400) return tr("http.generic4xx", "Não foi possível concluir o pedido. Tente novamente.");
            return null;
    }
}

/**
 * Extrai o mapa `campo -> mensagem` que o backend envia em `ApiError.fields`.
 *
 * Serve para marcar os inputs inválidos no formulário, em vez de só mostrar a
 * mensagem genérica ("Falha de validação no request."). Devolve `{}` quando a
 * resposta não traz erros por campo.
 */
export function getApiFieldErrors(error: unknown): Record<string, string> {
    const axiosErr = error as AxiosError<ApiErrorBody> | undefined;
    const fields = axiosErr?.isAxiosError ? axiosErr.response?.data?.fields : undefined;
    if (!Array.isArray(fields)) return {};

    return fields.reduce<Record<string, string>>((acc, item) => {
        if (item?.field && typeof item.message === "string") {
            acc[item.field] = item.message;
        }
        return acc;
    }, {});
}

/** Junta os erros por campo numa frase legível ("nome: obrigatório; nif: inválido"). */
function describeFieldErrors(fields: ApiFieldError[] | undefined): string | null {
    if (!Array.isArray(fields) || fields.length === 0) return null;

    const parts = fields
        .filter((item) => item?.field && item?.message)
        .map((item) => `${item.field}: ${item.message}`);

    return parts.length ? parts.join("; ") : null;
}

/** Tenta extrair uma mensagem legível de diferentes formatos de corpo. */
function pickMessage(data: ApiErrorBody | string | undefined): string | null {
    if (!data) return null;

    if (typeof data === "string") {
        return data.trim() || null;
    }

    // `fields` é mais específico que `message` (que costuma ser genérico neste
    // backend), por isso é anexado ao texto base quando ambos existem.
    const fieldDetail = describeFieldErrors(data.fields);
    const withFields = (base: string | null): string | null => {
        if (base && fieldDetail) return `${base.replace(/[.\s]+$/, "")} — ${fieldDetail}`;
        return base ?? fieldDetail;
    };

    // message: string | string[]
    if (Array.isArray(data.message)) {
        const joined = data.message.filter(Boolean).join(" ");
        if (joined) return withFields(joined);
    } else if (typeof data.message === "string" && data.message.trim()) {
        return withFields(data.message);
    }

    if (typeof data.detail === "string" && data.detail.trim()) {
        return withFields(data.detail);
    }

    if (typeof data.error === "string" && data.error.trim()) {
        return withFields(data.error);
    }

    if (fieldDetail) return fieldDetail;

    // errors: validação por campo (NestJS, class-validator, etc.)
    if (data.errors) {
        if (Array.isArray(data.errors)) {
            const joined = data.errors.filter(Boolean).join(" ");
            if (joined) return joined;
        } else if (typeof data.errors === "object") {
            const messages = Object.values(data.errors)
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .filter(Boolean);
            if (messages.length) return messages.join(" ");
        }
    }

    return null;
}

export function getErrorStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } })?.response?.status;
}

/**
 * Chave estável do erro (`tenant.unavailable`, `module.disabled`...), para tratar casos
 * específicos sem depender do texto traduzido. O gateway e o chat mandam `code`; o filtro de
 * tenant do gestaodeobras manda a chave em `error`, que nos outros erros é o texto do status HTTP.
 */
export function getApiErrorCode(error: unknown): string | undefined {
    const data = (error as AxiosError<ApiErrorBody> | undefined)?.response?.data;
    if (!data || typeof data !== "object") return undefined;
    if (typeof data.code === "string" && data.code) return data.code;
    return typeof data.error === "string" && /^[a-z]+(.[a-zA-Z]+)+$/.test(data.error) ? data.error : undefined;
}
